import React, { FC, useMemo } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';

import useDatasetColumnComponentTypesQuery from '../../../../hooks/datasets/useDatasetColumnComponentTypesQuery';
import useDatasetContentQuery from '../../../../hooks/datasets/useDatasetContentByIdQuery';
import useLabelsByComponentTypesQuery from '../../../../hooks/labels/useLabelsByComponentTypesQuery';

interface DatasetAlternateLabelViewProps {
    datasetId: number;
    datasetField: number;
    showLabels?: boolean;
}

interface AlternatesData {
    text: string;
    label: string;
}

const DatasetAlternateLabelView: FC<DatasetAlternateLabelViewProps> = ({
    datasetField,
    datasetId,
    showLabels = true,
}) => {
    const { data: datasetContent } = useDatasetContentQuery(datasetId);
    const {
        data: columnComponentTypes,
        isLoading: isColumnComponentTypesLoading,
    } = useDatasetColumnComponentTypesQuery(datasetId);

    const componentTypeIds = columnComponentTypes
        ? [
              ...new Set(
                  Object.values(columnComponentTypes).filter(
                      (id): id is string => !!id,
                  ),
              ),
          ]
        : undefined;

    const { labelsByComponentType } = useLabelsByComponentTypesQuery(
        componentTypeIds ?? [],
        {
            enabled: !isColumnComponentTypesLoading,
        },
    );
    const allLabels = useMemo<Record<number, string>>(
        () =>
            Object.values(labelsByComponentType)
                .flatMap((ct) => ct)
                .reduce((acc: Record<number, string>, label) => {
                    if (label.id !== null) {
                        acc[label.id] = label.label_name;
                    }
                    return acc;
                }, {}),
        [labelsByComponentType],
    );

    const alternatesData = useMemo<AlternatesData[]>(() => {
        return (datasetContent ?? []).map((item) => {
            const text = item.content?.[datasetField] ?? 'No text';
            const label = item.labels?.find(
                (val) => val.dataset_column === datasetField,
            );
            return {
                text,
                label: label?.label_id
                    ? allLabels[label?.label_id]
                    : 'No Label',
            };
        });
    }, [datasetContent, datasetField, allLabels]);
    return (
        <List>
            {alternatesData.map((alternate, index) => (
                <Item key={alternate.text} index={index}>
                    <ItemContainer>
                        <TextWrapper>
                            <EllipsisText tabIndex={1} title={alternate.text}>
                                {alternate.text}
                            </EllipsisText>
                        </TextWrapper>
                        {showLabels && (
                            <SelectContainer>
                                <EllipsisText tabIndex={1}>
                                    {alternate.label}
                                </EllipsisText>
                            </SelectContainer>
                        )}
                    </ItemContainer>
                </Item>
            ))}
        </List>
    );
};

const List = styled.div({
    height: '460px',
    overflow: 'auto',
    position: 'relative',
});

const Item = styled.div.withConfig({
    shouldForwardProp: (prop) => !['index'].includes(prop),
})<{ index: number }>(({ index }) => ({
    backgroundColor: aampeTheme.colors.white,
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderTop: index === 0 ? `1px solid ${aampeTheme.colors.bordersWG01}` : '',
    '&:hover': {
        backgroundColor: aampeTheme.colors.interactiveWG01,
    },
}));

const ItemContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    padding: aampeTheme.spacing4(0, 2, 0, 4),
});

const EllipsisText = styled.div(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: aampeTheme.spacing(1, 0),
    color: aampeTheme.colors.gray950,
    fontWeight: 400,
}));

const TextWrapper = styled.div({
    flex: 1,
    minWidth: 0,
});

const SelectContainer = styled.div({
    width: '220px',
    flexShrink: 0,
    overflow: 'hidden',
});

export default DatasetAlternateLabelView;
